<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import Tag from 'primevue/tag';

import { useAuctionEvent } from '@/shell/stores/auctionEvent';

const auctionEvent = useAuctionEvent();
const router = useRouter();
const route = useRoute();

// The countdown pill must keep ticking on its own, independent of whether
// any other frame (paused/resumed/closed/extended) happens to touch
// auctionEvent.event — `now` is the computed's only reactive time source
// (GT1-371).
const now = ref(Date.now());
let ticker: number | undefined;
onMounted(() => {
  ticker = window.setInterval(() => { now.value = Date.now(); }, 30_000);
});
onUnmounted(() => {
  if (ticker !== undefined) window.clearInterval(ticker);
});

const slug = computed(() => (route.params.slug as string | undefined) ?? auctionEvent.slug ?? '');
const title = computed(() => auctionEvent.event?.name ?? 'Auction');

function formatCountdown(ms: number): string {
  if (ms <= 0) return '';
  const totalMinutes = Math.floor(ms / 60_000);
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours < 24) return `${hours}h ${minutes}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

// Header pill reflects auction lifecycle.
//   open   → "Closing in [3d 23h]" success green, warn in last 5 min
//   paused → "Bidding paused", no countdown. The clock is stopped and the
//            close time may move when bidding resumes.
//   closed → "Auction closed", no countdown. Terminal — bidders get no further
//            lifecycle signal here; post-close work is admin-side only.
const pill = computed<{ severity: 'success' | 'warn' | 'secondary' | 'danger'; label: string; icon: string } | null>(() => {
  const ev = auctionEvent.event;
  if (!ev) return null;
  if (ev.status === 'closed') {
    return { severity: 'danger', label: 'Auction closed', icon: 'pi pi-lock' };
  }
  if (ev.status !== 'open' || !ev.closeAt) return null;
  if (ev.isPaused) return { severity: 'warn', label: 'Bidding paused', icon: 'pi pi-pause' };
  const remaining = new Date(ev.closeAt).getTime() - now.value;
  if (remaining <= 0) return null;
  const fiveMinMs = 5 * 60_000;
  return {
    severity: remaining <= fiveMinMs ? 'warn' : 'success',
    label: `Closing in ${formatCountdown(remaining)}`,
    icon: 'pi pi-clock',
  };
});

// Show a back button on any route that isn't one of the four bottom-nav
// roots. Item detail, sign-in, register, bid input, etc. all get it.
const tabRootSuffixes = ['/items', '/my-bids', '/wishlist', '/wins'];
const showBack = computed(() => !tabRootSuffixes.some((suffix) => route.path.endsWith(suffix)));

function onBack(): void {
  const prev = window.history.state?.back as string | null | undefined;
  if (prev) {
    router.back();
  } else if (slug.value) {
    router.replace(`/${slug.value}/items`);
  }
}

</script>

<template>
  <div
    class="strip"
    data-testid="context-strip"
  >
    <button
      v-if="showBack"
      type="button"
      class="strip__back"
      aria-label="Back"
      data-testid="context-strip__back"
      @click="onBack"
    >
      <i
        class="pi pi-arrow-left"
        aria-hidden="true"
      />
    </button>
    <span
      class="strip__title"
      data-testid="context-strip__title"
    >{{ title }}</span>
    <Tag
      v-if="pill"
      :severity="pill.severity"
      :value="pill.label"
      :icon="pill.icon"
      data-testid="context-strip__pill"
    />
  </div>
</template>

<style scoped>
.strip {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--app-space-8);
  padding: var(--app-space-12) var(--app-space-16) var(--app-space-4);
  min-height: 56px;
  width: 100%;
  max-width: var(--app-well-max-width);
  margin: 0 auto;
  box-sizing: border-box;
}
/* At tablet+ the strip becomes the left "name" block in the shell
   header (App.vue's .shell-header). Title + pill inline when there's
   room; flex-wrap drops the pill below the title when the column is
   shrunk to its minimum (sidenav width). */
@media (min-width: 641px) {
  .strip {
    max-width: none;
    margin: 0;
    padding: 0;
    min-height: 0;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--app-space-4) var(--app-space-12);
  }
  .strip__title {
    flex: 0 0 auto;
  }
}
.strip__back {
  all: unset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  flex-shrink: 0;
  color: var(--app-text-primary);
  font-size: var(--app-icon-size-md);
}
.strip__back:hover {
  background: var(--app-background-hover, rgba(0, 0, 0, 0.04));
}
.strip__back:focus-visible {
  outline: var(--app-border-width-default) solid var(--app-border-hover);
  outline-offset: 2px;
}
.strip__title {
  flex: 1;
  min-width: 0;
  font-size: var(--app-font-size-body-base);
  line-height: var(--app-line-height-body-base);
  font-weight: var(--app-font-weight-semibold);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
